import React, { useEffect, useState } from "react";
import { useParams, useLocation, useHistory } from "react-router-dom";
import { Breadcrumb, Col, Row, Button, Alert, Spinner } from "react-bootstrap";
import { Formik, Form, Field, ErrorMessage, useField } from "formik";
import * as Yup from "yup";

import API from "../Utility";
import PubHubAsset from "../PubHubAsset";

export default function EmailsView({ projectObject, setProjectObject }) {
  let { project } = useParams();
  const [submitSuccess, setSubmitSuccess] = useState(null);
  const [submitError, setSubmitError] = useState("");
  const [submittingForm, setSubmittingForm] = useState(false);
  const [projectConfig, setProjectConfig] = useState(
    projectObject && projectObject.config ? projectObject.config : { email: {} }
  );

  useEffect(() => {
    setProjectObject({
      ...projectObject,
      ...{
        config: projectConfig,
      },
    });
  }, [projectConfig]);

  // Initialize form validation
  let formValidation = {};

  const [headerImage, setHeaderImage] = useState(
    projectObject &&
      projectObject.config &&
      projectObject.config.email &&
      projectObject.config.email.headerImage
      ? projectObject.config.email.headerImage
      : undefined
  );

  const [footerImage, setFooterImage] = useState(
    projectObject &&
      projectObject.config &&
      projectObject.config.email &&
      projectObject.config.email.footerImage
      ? projectObject.config.email.footerImage
      : undefined
  );

  // Initialize form fields
  let formFields = {
    intro:
      projectConfig && projectConfig.email && projectConfig.email.intro
        ? projectConfig.email.intro
        : undefined,
    outro:
      projectConfig && projectConfig.email && projectConfig.email.outro
        ? projectConfig.email.outro
        : 'kindRegards',
    fromDisplayName:
      projectConfig && projectConfig.email && projectConfig.email.fromDisplayName
        ? projectConfig.email.fromDisplayName
        : undefined,
    fromEmailAddress:
      projectConfig && projectConfig.email && projectConfig.email.fromEmailAddress
        ? projectConfig.email.fromEmailAddress
        : undefined,
    teamName:
    projectConfig && projectConfig.email && projectConfig.email.teamName
      ? projectConfig.email.teamName
      : undefined,
    headerImage: headerImage,
    footerImage: footerImage,
  };

  const updateProjectTheme = async (values) => {
    try {
      const apiCallConfig = {
        url: `/admin/${project}/update`,
        data: values,
      };
      setSubmittingForm(true);
      const response = await API.put(apiCallConfig);
      if (response) {
        if (response === "Project updated successfully") {
          // success
          setSubmitSuccess("Email updated successfully");
        } else {
          setSubmitError(response.message || response);
        }
        setSubmittingForm(false);
      }
    } catch (error) {
      if (error.response) {
        setSubmitError(error.response.data.message);
      } else {
        setSubmitError("An error occurred, please try again");
      }
      setSubmittingForm(false);
    }
  };

  const handleSubmit = (values) => {
    let formattedData = {
      featuredImagePath: null,
      config: projectConfig ? {
        ...projectConfig,
        email: {}
      } : {},
    };
    setSubmitSuccess(null);
    setSubmitError(null);
    if (values) {
      if (values.intro && values.intro !== "") {
        formattedData.config = formattedData.config || { email: {} };
        formattedData.config.email.intro = values.intro;
      }
      if (values.outro && values.outro !== "") {
        formattedData.config = formattedData.config || { email: {} };
        formattedData.config.email.outro = values.outro;
      }
      if (values.fromDisplayName && values.fromDisplayName !== "") {
        formattedData.config = formattedData.config || { email: {} };
        formattedData.config.email.fromDisplayName = values.fromDisplayName;
      }
      if (values.fromEmailAddress && values.fromEmailAddress !== "") {
        formattedData.config = formattedData.config || { email: {} };
        formattedData.config.email.fromEmailAddress = values.fromEmailAddress;
      }
      if (values.teamName && values.teamName !== "") {
        formattedData.config = formattedData.config || { email: {} };
        formattedData.config.email.teamName = values.teamName;
      }
      if (headerImage) {
        formattedData.config = formattedData.config || { email: {} };
        formattedData.config.email.headerImage = headerImage;
      } else {
        formattedData.config = formattedData.config || { email: {} };
        formattedData.config.email.headerImage = undefined;
      }
      if (footerImage) {
        formattedData.config = formattedData.config || { email: {} };
        formattedData.config.email.footerImage = footerImage;
      } else {
        formattedData.config = formattedData.config || { email: {} };
        formattedData.config.email.footerImage = undefined;
      }
    }
    if (
      values &&
      !values.intro &&
      !values.outro &&
      !values.fromDisplayName &&
      !values.fromEmailAddress &&
      !values.teamName
    ) {
      formattedData.config = formattedData.config || { email: undefined };
    }
    if (formattedData && formattedData.config) {
      setProjectConfig(formattedData.config);
    }
    updateProjectTheme(formattedData);
  };

  return (
    <Row>
      <Col>
        <Formik
          initialValues={formFields}
          validationSchema={Yup.object().shape(formValidation)}
          onSubmit={(values, { setSubmitting }) => {
            setSubmitting(true);
            handleSubmit(values);
          }}
        >
          {({ touched, errors, isSubmitting }) => (
            <Form noValidate>
              {/* From Display Name */}
              <div className="form-group">
                <label htmlFor="fromDisplayName">From Display Name</label>
                <Field
                  type="text"
                  name="fromDisplayName"
                  placeholder={`From Display Name (e.g. ${projectObject && projectObject.name ? projectObject.name : "Company Name"})`}
                  className={`form-control ${
                    touched.fromDisplayName && errors.fromDisplayName
                      ? "is-invalid"
                      : ""
                  } ${
                    touched.fromDisplayName && !errors.fromDisplayName
                      ? "is-valid"
                      : ""
                  }`}
                ></Field>
                <ErrorMessage
                  component="div"
                  name="fromDisplayName"
                  className="invalid-feedback"
                />
              </div>

              {/* From Email Address */}
              <div className="form-group">
                <label htmlFor="fromEmailAddress">From Email Address</label>
                <Field
                  type="text"
                  name="fromEmailAddress"
                  placeholder={`From Email Address (e.g. noreply@${projectObject && projectObject.permalink ? projectObject.permalink : "companyname"}.com)`}
                  className={`form-control ${
                    touched.fromEmailAddress && errors.fromEmailAddress
                      ? "is-invalid"
                      : ""
                  } ${
                    touched.fromEmailAddress && !errors.fromEmailAddress
                      ? "is-valid"
                      : ""
                  }`}
                ></Field>
                <ErrorMessage
                  component="div"
                  name="fromEmailAddress"
                  className="invalid-feedback"
                />
              </div>

              {/* Header Image */}
              <div className="form-group">
                <label>{`Header Image (Use an image up to 1200 x 300 pixels)`}</label>
                <div className={`form-control asset-selector`}>
                  <PubHubAsset
                    asset={headerImage}
                    onChanged={(asset) => {
                      setHeaderImage(asset);
                    }}
                    maxFileSizeInBytes={5242880}
                    // acceptMimeTypes={["image/png", "image/jpeg"]}
                    assetType="image"
                  />
                </div>
              </div>

              {/* Intro */}
              <div className="form-group">
                <label htmlFor="intro">Intro</label>
                <Field
                  as="select"
                  name="intro"
                  className={`form-control ${
                    touched.intro && errors.intro
                      ? "is-invalid"
                      : ""
                  } ${
                    touched.intro && !errors.intro
                      ? "is-valid"
                      : ""
                  }`}
                >
                  <>
                    <option value={'firstName'}>
                      {'"Dear" + "First Name"'}
                    </option>
                    <option value={'username'}>
                      {'"Dear" + "Username"'}
                    </option>
                  </>
                </Field>
                <ErrorMessage
                  component="div"
                  name="intro"
                  className="invalid-feedback"
                />
              </div>

              {/* Outro */}
              <div className="form-group">
                <label htmlFor="outro">Outro</label>
                <Field
                  as="select"
                  name="outro"
                  className={`form-control ${
                    touched.outro && errors.outro
                      ? "is-invalid"
                      : ""
                  } ${
                    touched.outro && !errors.outro
                      ? "is-valid"
                      : ""
                  }`}
                >
                  <>
                    <option defaultValue={'kindRegards'} value={'kindRegards'}>
                      {'"Kind Regards"'}
                    </option>
                  </>
                </Field>
                <ErrorMessage
                  component="div"
                  name="outro"
                  className="invalid-feedback"
                />
              </div>

              {/* Team Name */}
              <div className="form-group">
                <label htmlFor="teamName">Team Name</label>
                <Field
                  type="text"
                  name="teamName"
                  placeholder={`Team Name (e.g. ${projectObject && projectObject.name ? projectObject.name : "Company Name"} Team)`}
                  className={`form-control ${
                    touched.teamName && errors.teamName
                      ? "is-invalid"
                      : ""
                  } ${
                    touched.teamName && !errors.teamName
                      ? "is-valid"
                      : ""
                  }`}
                ></Field>
                <ErrorMessage
                  component="div"
                  name="teamName"
                  className="invalid-feedback"
                />
              </div>

              {/* Footer Image */}
              <div className="form-group">
                <label>{`Footer Image (Use an image up to 1200 x 300 pixels)`}</label>
                <div className={`form-control asset-selector`}>
                  <PubHubAsset
                    asset={footerImage}
                    onChanged={(asset) => {
                      setFooterImage(asset);
                    }}
                    maxFileSizeInBytes={5242880}
                    // acceptMimeTypes={["image/png", "image/jpeg"]}
                    assetType="image"
                  />
                </div>
              </div>

              {submitError && <Alert variant={"danger"}>{submitError}</Alert>}
              {submitSuccess && (
                <Alert variant={"success"}>{submitSuccess}</Alert>
              )}

              <div className="form-group">
                {submittingForm ? (
                  <Spinner animation="border" role="status">
                    <span className="sr-only">Loading...</span>
                  </Spinner>
                ) : (
                  <>
                    <button
                      type="submit"
                      className={`mb-2 btn btn-outline-primary ${
                        submittingForm ? "hide" : ""
                      }`}
                      style={{ marginRight: "5px" }}
                      disabled={submittingForm ? true : false}
                    >
                      Save
                    </button>
                  </>
                )}
              </div>
            </Form>
          )}
        </Formik>
      </Col>
    </Row>
  );
}
